import { useWallet } from '@txnlab/use-wallet-react'
import dayjs from 'dayjs'
import * as React from 'react'
import useAddresses from 'api/hooks/useAddresses'
import useAnalytics from 'api/hooks/useAnalytics'
import useInfiniteAnalytics, { InfiniteAnalyticsQueryResult } from 'api/hooks/useInfiniteAnalytics'
import useInfiniteSearch from 'api/hooks/useInfiniteSearch'
import { useOfferStore } from 'store/state/offerStore'
import { getGridLimit, getListLimit } from 'components/Dashboard/Dashboard.utils'
import { getGridCellHeight } from 'components/NfdResult/NfdResult.utils'
import { isBreakpoint } from 'helpers/utilities'
import { gridProperties } from 'components/ItemLayout/ItemLayout.constants'
import { ViewSetting } from 'components/Dashboard/Controls'
import { ResultsSize } from 'components/Dashboard/Dashboard.types'
import {
  ManageDashboardCounts,
  ManageDashboardQueryParams,
  ManageDashboardTabId,
  ManageDashboardView
} from './ManageDashboard.types'
import type { InfiniteData } from '@tanstack/react-query'

interface UseManageDashboard {
  resultsSize: ResultsSize
  viewSetting: ViewSetting
  current: ManageDashboardTabId
  search: string
}

export default function useManageDashboard({
  resultsSize,
  viewSetting,
  current,
  search
}: UseManageDashboard) {
  const { activeAccount } = useWallet()
  const activeWallet = activeAccount?.address

  const getLimit = (view: ManageDashboardTabId) => {
    switch (view) {
      case 'sales':
        return getTableLimit({ resultsSize })

      case 'offers':
        return getTableLimit({ resultsSize })

      default:
        return getGridListLimit({ viewSetting, resultsSize })
    }
  }

  const getSearchText = (view: ManageDashboardTabId) => {
    return view === current ? search : ''
  }

  const owned = useManageOwned({
    activeWallet,
    limit: getLimit('owned'),
    search: getSearchText('owned')
  })

  const expirations = useManageExpirations({
    activeWallet,
    limit: getLimit('expirations'),
    search: getSearchText('expirations')
  })

  const forSale = useManageForSale({
    activeWallet,
    limit: getLimit('forSale'),
    search: getSearchText('forSale')
  })

  const reserved = useManageReserved({
    activeWallet,
    limit: getLimit('reserved'),
    search: getSearchText('reserved')
  })

  const sales = useManageSales({ activeWallet, limit: getLimit('sales') })

  const offersSent = useManageOffersSent({ activeWallet, limit: getLimit('offers') })

  const offersReceived = useManageOffersReceived({ activeWallet, limit: getLimit('offers') })

  const queries = React.useMemo(() => {
    return {
      owned,
      expirations,
      forSale,
      reserved,
      sales,
      offersSent,
      offersReceived
    }
  }, [expirations, forSale, offersReceived, offersSent, owned, reserved, sales])

  const usersQuery = useManageUsers({
    salesData: sales.query.data,
    offersSentData: offersSent.query.data,
    offersReceivedData: offersReceived.query.data
  })

  const sentOffersCount = useSentOffersCount(activeWallet)
  const receivedOffersCount = useReceivedOffersCount(activeWallet)

  const offersCount = React.useMemo(
    () => sentOffersCount + receivedOffersCount,
    [receivedOffersCount, sentOffersCount]
  )

  const counts = React.useMemo(() => {
    return Object.values(ManageDashboardView).reduce((acc, view) => {
      const count = view === 'offers' ? offersCount : queries[view].total ?? 0

      return { ...acc, [view]: count }
    }, {} as ManageDashboardCounts)
  }, [offersCount, queries])

  return {
    queries,
    counts,
    usersQuery,
    activeWallet
  }
}

function useManageOwned({ activeWallet, limit, search }: ManageDashboardQueryParams) {
  return useInfiniteSearch({
    queryKey: ['manage-owned', activeWallet, { limit, search }],
    params: {
      owner: activeWallet,
      state: ['owned'],
      view: 'brief',
      limit,
      ...(search !== '' ? { prefix: search } : {})
    },
    options: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: activeWallet !== undefined && limit > 0
    }
  })
}

function useManageExpirations({ activeWallet, limit, search }: ManageDashboardQueryParams) {
  return useInfiniteSearch({
    queryKey: ['manage-expires-soon', activeWallet, { limit, search }],
    params: {
      owner: activeWallet,
      view: 'brief',
      sort: 'expiresAsc',
      expiresBefore: dayjs().add(30, 'days').toISOString(),
      limit,
      ...(search !== '' ? { prefix: search } : {})
    },
    options: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: activeWallet !== undefined && limit > 0
    }
  })
}

function useManageForSale({ activeWallet, limit, search }: ManageDashboardQueryParams) {
  return useInfiniteSearch({
    queryKey: ['manage-for-sale', activeWallet, { limit, search }],
    params: {
      owner: activeWallet,
      state: ['forSale'],
      view: 'brief',
      limit,
      ...(search !== '' ? { prefix: search } : {})
    },
    options: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: activeWallet !== undefined && limit > 0
    }
  })
}

function useManageReserved({ activeWallet, limit, search }: ManageDashboardQueryParams) {
  return useInfiniteSearch({
    queryKey: ['manage-reserved', activeWallet, { limit, search }],
    params: {
      reservedFor: activeWallet,
      view: 'brief',
      limit,
      ...(search !== '' ? { prefix: search } : {})
    },
    options: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: activeWallet !== undefined && limit > 0
    }
  })
}

function useManageSales({ activeWallet, limit }: ManageDashboardQueryParams) {
  return useInfiniteAnalytics({
    queryKey: ['manage-sales', activeWallet, { limit }],
    params: {
      event: ['sold'],
      sort: 'timeDesc',
      seller: activeWallet,
      limit
    },
    options: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: activeWallet !== undefined && limit > 0
    }
  })
}

function useManageOffersSent({ activeWallet, limit }: ManageDashboardQueryParams) {
  const lastViewedSent = useOfferStore((state) => state.lastViewedSent)
  const afterTime = lastViewedSent[activeWallet || '']

  return useInfiniteAnalytics({
    queryKey: ['manage-offers-sent', activeWallet, { limit, afterTime }],
    params: {
      event: ['postedOffer'],
      sort: 'priceDesc',
      buyer: activeWallet,
      afterTime,
      includeOwner: true,
      limit
    },
    options: {
      keepPreviousData: true,
      enabled: activeWallet !== undefined && limit > 0,
      // only include offers that have not been accepted
      select: (data) => {
        const pages = data.pages.reduce((acc, page) => {
          const { data, nextPage } = page
          const results =
            data?.results?.filter((offer) => offer.data?.currentOwner !== activeWallet) || []

          return [...acc, { data: { ...data, results }, nextPage }]
        }, [] as InfiniteAnalyticsQueryResult[])

        return { ...data, pages }
      }
    }
  })
}

function useManageOffersReceived({ activeWallet, limit }: ManageDashboardQueryParams) {
  const lastViewedReceived = useOfferStore((state) => state.lastViewedReceived)
  const afterTime = lastViewedReceived[activeWallet || '']

  return useInfiniteAnalytics({
    queryKey: ['manage-offers-received', activeWallet, { limit, afterTime }],
    params: {
      event: ['postedOffer'],
      sort: 'priceDesc',
      seller: activeWallet,
      afterTime,
      includeOwner: true,
      limit
    },
    options: {
      keepPreviousData: true,
      enabled: activeWallet !== undefined && limit > 0,
      // only include offers that have not been accepted
      select: (data) => {
        const pages = data.pages.reduce((acc, page) => {
          const { data, nextPage } = page
          const results =
            data?.results?.filter((offer) => offer.data?.currentOwner === activeWallet) || []

          return [...acc, { data: { ...data, results }, nextPage }]
        }, [] as InfiniteAnalyticsQueryResult[])

        return { ...data, pages }
      }
    }
  })
}

interface UseManageUsersParams {
  salesData: InfiniteData<InfiniteAnalyticsQueryResult> | undefined
  offersSentData: InfiniteData<InfiniteAnalyticsQueryResult> | undefined
  offersReceivedData: InfiniteData<InfiniteAnalyticsQueryResult> | undefined
}

function useManageUsers({ salesData, offersSentData, offersReceivedData }: UseManageUsersParams) {
  const salesUsers = React.useMemo(() => {
    const allSales =
      salesData?.pages?.flatMap((page) => page.data.results).filter((sale) => sale !== null) || []

    return allSales.map((sale) => sale.data?.buyer) || []
  }, [salesData])

  const offersSentUsers = React.useMemo(() => {
    const allSentOffers =
      offersSentData?.pages
        ?.flatMap((page) => page.data.results)
        .filter((offer) => offer !== null) || []

    return allSentOffers.map((offer) => offer.data?.seller) || []
  }, [offersSentData])

  const offersReceivedUsers = React.useMemo(() => {
    const allReceivedOffers =
      offersReceivedData?.pages
        ?.flatMap((page) => page.data.results)
        .filter((offer) => offer !== null) || []

    return allReceivedOffers.map((offer) => offer.data?.buyer) || []
  }, [offersReceivedData])

  const allAddresses = React.useMemo(() => {
    return [...new Set([...salesUsers, ...offersSentUsers, ...offersReceivedUsers])] as string[]
  }, [offersReceivedUsers, offersSentUsers, salesUsers])

  return useAddresses({
    params: {
      address: allAddresses,
      view: 'thumbnail'
    },
    options: {
      enabled: allAddresses.length > 0
    }
  })
}

function useSentOffersCount(activeWallet: string | undefined) {
  const lastViewedSent = useOfferStore((state) => state.lastViewedSent)

  const offersSentCountQuery = useAnalytics({
    params: {
      event: ['postedOffer'],
      sort: 'priceDesc',
      buyer: activeWallet,
      afterTime: lastViewedSent[activeWallet || ''],
      includeOwner: true,
      limit: 200
    },
    options: {
      enabled: activeWallet !== undefined,
      // only include offers that have not been accepted
      select: (data) => {
        const results = data.results?.filter((offer) => offer.data?.currentOwner !== activeWallet)

        return { results, total: results?.length }
      }
    }
  })

  return offersSentCountQuery.data?.total || 0
}

function useReceivedOffersCount(activeWallet: string | undefined) {
  const lastViewedReceived = useOfferStore((state) => state.lastViewedReceived)

  const offersReceivedCountQuery = useAnalytics({
    params: {
      event: ['postedOffer'],
      sort: 'priceDesc',
      seller: activeWallet,
      afterTime: lastViewedReceived[activeWallet || ''],
      includeOwner: true,
      limit: 200
    },
    options: {
      enabled: activeWallet !== undefined,
      // only include offers that have not been accepted
      select: (data) => {
        const results = data.results?.filter((offer) => offer.data?.currentOwner === activeWallet)

        return { results, total: results?.length }
      }
    }
  })

  return offersReceivedCountQuery.data?.total || 0
}

interface GetLimitParams {
  viewSetting: ViewSetting
  resultsSize: ResultsSize
  min?: number
  max?: number
}

function getGridListLimit({ viewSetting, resultsSize, min = 4, max = 200 }: GetLimitParams) {
  const { width, height } = resultsSize

  if (width === 0 || height === 0) {
    return 0
  }

  const { columnGap, rowGap } = gridProperties

  const gridLimit = getGridLimit({
    width,
    height,
    columnGap,
    rowGap,
    cellMinWidth: isBreakpoint('xl') ? 200 : 160,
    cellHeight: getGridCellHeight
  })

  const listLimit = getListLimit({ height, rowHeight: 80 })

  const calculatedLimit = viewSetting === 'grid' ? gridLimit : listLimit

  const limit = Math.min(Math.max(calculatedLimit, min), max)

  return limit
}

function getTableLimit({ resultsSize, min = 4, max = 200 }: Omit<GetLimitParams, 'viewSetting'>) {
  const { height } = resultsSize

  if (height === 0) {
    return 0
  }

  const tableHeaderHeight = isBreakpoint('sm') ? 80 : 0
  const theadHeight = 49
  const tableRowMaxHeight = 69
  const maxTbodyHeight = height - tableHeaderHeight - theadHeight

  const calculatedLimit = Math.floor(maxTbodyHeight / tableRowMaxHeight)

  const limit = Math.min(Math.max(calculatedLimit, min), max)

  return limit
}
